import { data as routerData } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import { useEffect, lazy, Suspense, useState } from 'react';
import { useHydrated } from 'remix-utils';
import { useLoaderData, useLocation, useSearchParams } from 'react-router';
import { useInView } from 'react-intersection-observer';
import { getPreloadImageLink } from '~/modules/images';
import {
    mergeMeta,
    createMetaTitle,
    createMetaDescription,
    fallbackOGSocialImage,
    createOGTags,
    getCanonicalHreflang,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createVideoSchema } from '~/modules/seo/schema/video-schema';
import { collectModuleVideos } from '~/modules/seo/util/collect-module-videos';
import { createProjectSchema } from '~/modules/seo/schema/project-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import Container from '@modules/project/component/container/Container';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import makeProject from '@modules/project/util/make-project';
import getProjectQuery, {
    PROJECT_REVAMP_BANNER,
} from '@modules/project/util/get-project-query';
import getFeaturedProductsQuery from '@modules/project/util/get-featured-products-query';
import getFeaturedProfessionalsQuery from '@modules/project/util/get-featured-professionals-query';
import getFeaturedProjectsQuery from '@modules/project/util/get-featured-projects-query';
import { getPopularProjects } from '~/modules-rocco/projects/api/get-popular-projects.server';
import makeFeaturedProducts from '@modules/project/util/make-featured-products.server';
import makeFeaturedProjects from '@modules/project/util/make-featured-projects';
import {
    fetchSimilarProjectsPage,
    SIMILAR_PROJECTS_PAGE_SIZE,
} from '@modules/project/util/fetch-similar-projects.server';
import makeFeaturedProfessionals from '@modules/project/util/make-featured-professionals';
import DetailModules from '~/modules/page-builder/component/detail/detail-modules/DetailModules';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import {
    getCanDownloadImages,
    getPermissions,
    useEditPermissions,
    usePermissions,
} from '~/modules/user';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { PermissionEnum } from 'generated/graphql';
import getMemberRecentlyTagged from '@modules/product-tagging/api/get-member-recently-tagged.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { PaintPickerContextProvider as PaintPickerContextProviderV2 } from '@modules/paint-picker-new/context/PaintPickerContext';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import { useRefreshOnRotate } from '@modules/root/util/use-refresh-on-rotate';
import invariant from 'tiny-invariant';
import getProjectBuilderModulesQuery from '@modules/project/util/get-project-builder-modules.query';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import { getPrimaryCTA } from '@modules/professional/util/professional-cta-helper';
import { PrimaryCtaEnum } from 'generated/graphql';
import { adsRequest } from '~/modules/adverts/api/ads-request.server';
import getModuleWithAds from '@modules/project/util/get-module-with-ads';
import { useMemo, useRef } from 'react';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { makeProjectItemData } from '~/modules/adverts/utils/get-item-data.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { APHandle } from '~/modules/root/type/handle';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { useGateSubject } from '~/modules-rocco/user/auth-gate/gate-subject';
import { useProjectDirectoryAdminToggles } from '@modules/project/hook/use-project-directory-admin-toggles';
import { getSession } from '~/modules/root/util/session.server';
import dayjs from 'dayjs';
// `dayjs(str, 'D-MM-YY')` only honours the format argument when the customParseFormat
// plugin is registered, and `dayjs.extend` mutates global dayjs. This route must register
// it in its own module: otherwise the plugin is present in the SSR bundle but tree-shaken
// out of the client route chunk, so the server parses `PublishDate` to a valid ISO date
// while the client yields "Invalid Date". The resulting JSON-LD `<script>` content differs
// between server and client and breaks hydration. Registering it here keeps parsing
// identical on both sides.
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import type { ProjectAd } from '@modules/adverts/type';
import ProjectPageHeader from '~/modules/project/component/page-header/ProjectPageHeader';
import { useSetAtom } from 'jotai';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { getProjectTeamMembers } from '~/modules/professional/api/get-project-team-members.server';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import { splitContentByChars } from '~/modules/project/util/split-content';
import { getDirectoryItemByIDs } from '~/modules/directory/api/get-directory-items-admin.server';
import { getDirectoryFeaturedProjects } from '~/modules/directory/api/get-directory-featured-project.server';
import ProjectPageContent from '~/modules/project/component/content/ProjectPageContent';
import { hasFeature } from '@modules/root';
import { ProjectPageRevamp } from '~/ui-rocco/pages/project-detail/revamp/ProjectPageRevamp';

import { ProjectDesktopWebsiteCTA } from '~/modules/project/component/website-cta/ProjectDesktopWebsiteCTA';
// This section is server-rendered, so it must ship in the route's main chunk: a separately
// lazy-loaded chunk that fails to arrive on the client leaves the server markup unhydratable
// and drops the whole "Views and Engagement" section. Bundling it here keeps client hydration
// in step with the server output.
import { ProjectSharesAndTags } from '~/modules/project/component/details-accordion/ProjectSharesAndTags';

/**
 * Lazy-loaded components for below-fold mobile content.
 * These reduce initial bundle size and defer rendering until needed.
 */

const PageBottomSection = lazy(() =>
    import('~/ui-rocco/pages/shared/PageBottomSection').then((mod) => ({
        default: mod.PageBottomSection,
    })),
);

/**
 * SSR-aware lazy section that defers rendering of below-fold content.
 *
 * Behavior:
 * - SSR: Always renders children (for SEO crawlers)
 * - Client after hydration: Uses IntersectionObserver to lazy-load content
 *
 * This ensures crawlers see full content while users benefit from lazy loading.
 *
 * Note: We delay switching to "lazy mode" until the observer has had a chance
 * to fire, preventing a flash where content briefly shows skeleton after hydration.
 */
const LazySection = ({
    children,
    fallback,
    rootMargin = '200px',
    className,
}: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    rootMargin?: string;
    className?: string;
}) => {
    const isHydrated = useHydrated();
    // Track when hydration has "settled" (observer had time to fire)
    const [hydrationSettled, setHydrationSettled] = useState(false);

    const { ref, inView } = useInView({
        rootMargin,
        triggerOnce: true,
        skip: !isHydrated, // Skip observation during SSR
    });

    useEffect(() => {
        if (isHydrated && !hydrationSettled) {
            // Give IntersectionObserver time to detect initial visibility
            // before switching to lazy mode. This prevents the flash where
            // content briefly shows skeleton right after hydration.
            const timer = requestAnimationFrame(() => {
                setHydrationSettled(true);
            });
            return () => cancelAnimationFrame(timer);
        }
    }, [isHydrated, hydrationSettled]);

    // SSR: always render children for SEO
    // Hydration not settled: keep rendering to prevent flash
    // Hydration settled: render based on visibility
    const shouldRender = !isHydrated || !hydrationSettled || inView;

    return (
        <div ref={ref} className={className}>
            {shouldRender ? children : fallback}
        </div>
    );
};

/** Skeleton placeholder for below-fold content sections */
const BelowFoldSkeleton = () => (
    <div className="flex flex-col gap-8 px-4">
        <div className="h-32 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-48 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
    </div>
);

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const project = data?.project;
        const projectTitle =
            project?.Title &&
            project?.Professional?.Title &&
            `${project?.Title} by ${project?.Professional?.Title}`;

        const title = createMetaTitle(props, [
            projectTitle,
            data?.localisation?.Title,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.project?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.project?.BannerImages?.[0]?.LibraryImageFile
                ?.DetailBannerStandard?.Desktop,
        );

        // Serialise as a bare calendar date (no time/zone). `.format()` with no
        // args emits the local UTC offset, which differs between the UTC server
        // (+00:00) and an NZ/AU browser (+12:00/+10:00); that makes the ld+json
        // `<script>` text mismatch on hydration (React #418), which bails the
        // route's client render and drops below-fold content (e.g. the Views &
        // Engagement section). The source granularity is a date anyway (D-MM-YY).
        const projectPublishDate = data?.project?.PublishDate
            ? dayjs(data.project.PublishDate, 'D-MM-YY').format('YYYY-MM-DD')
            : undefined;

        // The project's publish date stands in when a video module carries no date of its own, since a
        // VideoObject without an upload date is rejected outright by Google.
        const videoSchemas = collectModuleVideos(project?.Modules).map(
            (video) =>
                createVideoSchema({
                    name: video.title ?? data?.project?.Title ?? '',
                    publishDate: video.dateCreated ?? projectPublishDate,
                    professionalName: project?.Professional?.Title,
                    url: video.url,
                    thumbnailUrl: video.thumbnailUrl,
                    description: video.title || description,
                }),
        );

        // CreativeWork JSON-LD for the project itself (the professional is the creator, the gallery the
        // images), so the largest indexable surface is eligible for richer results and Google Images.
        const baseUrl = rootData ? `https://${rootData.env.host}` : '';
        // FacebookPreviewImage is stored site-relative, so make it absolute for JSON-LD `image`
        // (mirrors professional-schema). fallbackSocialImage is already absolute, so leave it as-is.
        const projectImage = data?.project?.FacebookPreviewImage
            ? baseUrl + data.project.FacebookPreviewImage
            : fallbackSocialImage;
        const projectLocation = [
            project?.Location?.Suburb,
            project?.Location?.Region,
            project?.Location?.Country,
        ]
            .filter(Boolean)
            .join(', ');

        const projectSchema = project
            ? createProjectSchema({
                  url: baseUrl + props.location.pathname,
                  name: project.Title ?? '',
                  description,
                  images: projectImage ? [projectImage] : [],
                  professionalName: project.Professional?.Title ?? '',
                  datePublished: projectPublishDate,
                  category: project.Categories?.[0]?.PrettyTitle ?? undefined,
                  location: projectLocation || undefined,
              })
            : [];

        const ldSchema = createLDSchema([...videoSchemas, projectSchema]);

        const bannerImage = data?.project?.Banner?.imageUrl;

        const preloadImages =
            data?.deviceType === 'mobile'
                ? getPreloadImageLink(
                      undefined,
                      bannerImage,
                      '100vw', // Mobile banner is full viewport width
                      { mobileWidth: 414 },
                  )
                : getPreloadImageLink(
                      bannerImage,
                      undefined,
                      getImagesSizes(1820),
                      { desktopWidth: 1820 },
                  );

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.project?.FacebookPreviewImage ?? fallbackSocialImage,
                type: 'website',
            }),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.project?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.project?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation,
            ),
        ];
    },
);

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    hideContactArchiPro: true,
    // The revamp uses the unified header's logomark so the brand mark is
    // consistent with the rest of the editorial page. Resolved against loader
    // data so the legacy page keeps the compact header while
    // `revamp_2026` is off.
    header: (loaderData) =>
        (loaderData as { detailsRevamp?: boolean } | undefined)?.detailsRevamp
            ? { showLogomark: true }
            : undefined,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { params, context, request } = args;
    invariant(!!params.urlSegment, 'A project URL segment is required');
    const { appDisplayMode, user, featureFlags } = context;
    const isMobile = appDisplayMode === 'mobile';

    const searchParams = new URL(request.url).searchParams;
    const revampActive =
        hasFeature(featureFlags, 'revamp_2026') ||
        (process.env.NODE_ENV === 'development' &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    // The revamp's hero wants the 2x banner; the legacy page keeps the
    // query's own frame.
    const projectPromise = getProjectQuery(
        args,
        false,
        false,
        revampActive ? PROJECT_REVAMP_BANNER : undefined,
    );

    // Chained off the project query — these only need its ID / category link,
    // so they overlap the other parallel queries instead of running after all
    // of them plus makeProject.
    const rawProjectPromise = projectPromise.then((result) => {
        const node = result.data?.Directory?.Project;
        return node?.__typename === 'Project' ? node : null;
    });
    const projectTeamMembersPromise = rawProjectPromise.then(async (node) => {
        if (!node) return [];
        const teamMembers = await getProjectTeamMembers({
            projectId: String(node.ID),
            ...args.context,
        });
        return teamMembers ?? [];
    });
    const projectKPIPromise = rawProjectPromise.then((node) =>
        node
            ? getDirectoryItemByIDs({ IDs: [node.ID], ...args.context })
            : null,
    );
    // Similar projects only render on the revamp page — skip the extra
    // directory search for legacy requests.
    const similarProjectsPromise = rawProjectPromise.then((node) => {
        if (!revampActive || !node) return null;
        return fetchSimilarProjectsPage({
            context,
            categoryLink: node.Categories?.[0]?.Link ?? '',
            currentProjectId: node.ID,
            offset: 0,
            limit:
                context.appDisplayMode === 'mobile'
                    ? SIMILAR_PROJECTS_PAGE_SIZE.mobile
                    : SIMILAR_PROJECTS_PAGE_SIZE.desktop,
        });
    });
    const [
        projectResult,
        featuredProductsResult,
        featuredProfessionalsResult,
        featuredProjectsResult,
        popularProjectsResult,
        memberRecentlyTagged,
        localisation,
        projectBuilderModulesResult,
        directoryFeaturedResult,
        projectAds,
        projectTeamMembers,
        projectKPI,
        similarProjects,
    ] = await Promise.all([
        projectPromise,
        getFeaturedProductsQuery(args),
        getFeaturedProfessionalsQuery(args),
        getFeaturedProjectsQuery(args),
        getPopularProjects({ limit: 10 }, context),
        getMemberRecentlyTagged(args),
        getPageLocalisation(args),
        // Guest responses are cacheable: MultiColumnModule and ModuleColumn now purge the owning
        // project's page cache tags on edit, so module changes are reflected without waiting for the TTL.
        getProjectBuilderModulesQuery(args, false, false),
        getDirectoryFeaturedProjects(context),
        (async () => {
            // waterfall request to get the project data and feed it to the Ads API.
            // Avoid duplicated heavy ORM queries for the project data in the Ads BE.
            const projectResponse = await projectPromise;
            unsafeVariant_DO_NOT_USE(projectResponse);

            const projectItemData = await makeProjectItemData(
                args,
                projectResponse,
            );

            return adsRequest({
                type: 'ProjectPlacementGroup',
                requestUrl: request.url,
                item: projectItemData,
                ...args.context,
            });
        })(),
        projectTeamMembersPromise,
        projectKPIPromise,
        similarProjectsPromise,
    ]);

    unsafeVariant_DO_NOT_USE(featuredProductsResult);
    unsafeVariant_DO_NOT_USE(featuredProfessionalsResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);
    unsafeVariant_DO_NOT_USE(projectResult);

    invariant(
        !!projectBuilderModulesResult,
        'Project builder modules not found',
    );

    const project = await makeProject(
        args,
        projectResult,
        projectBuilderModulesResult,
        user,
    );

    // Determine if project has a valid banner (image or video)
    const hasBanner = !!(
        project.Banner?.imageUrl || project.Banner?.videoBannerUrl
    );

    const isPinnedToDirectory =
        directoryFeaturedResult &&
        'directoryFeaturedProjects' in directoryFeaturedResult
            ? directoryFeaturedResult.directoryFeaturedProjects.some(
                  (fp) => fp.ProjectID === project.ID,
              )
            : false;

    const { firstCol, secondCol, isOverflowing } = splitContentByChars(
        project.Content ?? '',
    );

    // Await all remaining data in parallel for SEO optimization
    const [featuredProducts, featuredProfessionals, featuredProjects] =
        await Promise.all([
            makeFeaturedProducts(
                args,
                featuredProductsResult,
                project.Title,
                user,
            ),
            makeFeaturedProfessionals(
                args,
                featuredProfessionalsResult,
                project,
            ),
            makeFeaturedProjects(args, featuredProjectsResult, project),
        ]);

    const primaryCTA = getPrimaryCTA({
        primaryCTA: PrimaryCtaEnum.AutoOptimise,
        pageType: 'project',
    });

    const showPageNotLiveWarning = getPermissions(
        user,
        PermissionEnum.BmStaffView,
        project.Professional.ID,
    );

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        project.Professional?.Country || 'NZ',
        context.localisation.country,
        !project.IsLive || project.Disable || project.Professional?.Disable,
        'project',
        false,
        {
            showPageNotLiveWarning: showPageNotLiveWarning,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        },
    );

    const ads = projectAds ? getProjectPlacements(projectAds, isMobile) : [];

    const session = await getSession(request);

    return routerData(
        {
            project,
            deviceType: context.device.type,
            projectKPI,
            summary: {
                firstCol,
                secondCol,
                isOverflowing,
            },
            featuredProducts,
            featuredProfessionals,
            featuredProjects,
            similarProjects,
            popularProjects: popularProjectsResult,
            memberRecentlyTagged,
            moduleMeta: project.meta,
            memberPermissions: {
                canCreateTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingCreate,
                    project.Professional.ID,
                ),
                canDeleteTag: getPermissions(
                    user,
                    PermissionEnum.BmProductTaggingDelete,
                    project.Professional.ID,
                ),
                canDownloadImages: getCanDownloadImages(
                    user,
                    'project',
                    project.Professional.ID,
                ),
            },
            localisation,
            primaryCTA,
            ads,
            warningMessages,
            projectTeamMembers,
            isPinnedToDirectory,
            pageTheme: hasBanner ? 'transparent' : 'default',
            // The editorial revamp runs on a dark (#181818) page, so its solid
            // header goes near-black with light icons; the legacy light page
            // keeps the default light header. Gated on the flag so it tracks
            // whichever variant actually renders.
            headerSolidTheme: hasFeature(featureFlags, 'revamp_2026')
                ? ('dark' as const)
                : undefined,
            // Umbrella flag for the whole details revamp (BC-4505).
            detailsRevamp: hasFeature(featureFlags, 'revamp_2026'),
        },
        {
            headers: await session.getHeaders(),
        },
    );
};

/**
 * Renders page builder modules with advertisements.
 * On mobile, splits modules into initial (above-fold) and deferred (below-fold) sets
 * to improve initial page load performance.
 */
const ModuleAds = ({
    modules,
    ads,
    isMobile,
}: {
    modules: PageBuilderModuleType[];
    ads: ProjectAd[];
    isMobile: boolean;
}) => {
    const location = useLocation();
    const hideAd = location.state?.hideAd;

    // Keep the first value returned by loader to avoid changing when loader revalidates
    const adsMemoized = useLocationMemo(hideAd ? [] : ads);
    // Render ads in the browser only (BC-1531), but keep them out of BOTH the SSR HTML and
    // the first client render so the server and client hydrate to identical DOM. The old
    // `typeof document === 'undefined' ? []` guard injected ads immediately on the client,
    // so whenever real ads existed the client's first render differed from the server — a
    // hydration mismatch (React #418) that bailed the route's client render and dropped the
    // below-fold Suspense subtree (e.g. the "Views and Engagement" section). Adding ads on
    // the post-hydration render keeps them browser-only without diverging from the server.
    const isHydrated = useHydrated();
    const modulesWithAds = useMemo(
        () => getModuleWithAds(modules, isHydrated ? adsMemoized : []),
        [modules, adsMemoized, isHydrated],
    );

    // Render all modules - LazyImageModule handles individual lazy loading
    // with content-visibility:auto and IntersectionObserver per module
    return <DetailModules isMobile={isMobile} modules={modulesWithAds} />;
};

const ProjectPageLegacy = () => {
    const { desktop: isDesktop, mobile: isMobile } = useAppDisplayModeContext();
    const { memberRecentlyTagged, memberPermissions, ads, moduleMeta } =
        useLoaderData<typeof loader>();

    const project = useProjectPreviewRouteData();

    const { canCreateTag, canDeleteTag, canDownloadImages } = memberPermissions;
    const canDownloadBannerImage = !isMobile && canDownloadImages;

    const PickerContext = PaintPickerContextProviderV2;

    const modulesContainerRef = useRef<HTMLDivElement>(null);
    const openLogin = useOpenLogin();

    const projectModules = (
        <ProductTaggingContextProvider
            canCreateTag={isDesktop && canCreateTag}
            canDeleteTag={canDeleteTag}
            recentlyTaggedProducts={memberRecentlyTagged.products}
            recentlyTaggedProfessionals={memberRecentlyTagged.professionals}
        >
            <ModulesContextProvider meta={moduleMeta}>
                {isDesktop && (
                    <>
                        <div className="mx-12 mb-2 font-display text-6.5 leading-[130%] font-normal text-[#141414]">
                            Project Gallery
                        </div>
                        <div className="relative mx-12 border-t border-[#141414]" />
                    </>
                )}
                <div
                    ref={modulesContainerRef}
                    id="project-gallery"
                    className="mt-11.5 -scroll-mt-32 md:mt-11.5 md:mb-24"
                >
                    <ModuleAds
                        isMobile={isMobile}
                        modules={project.Modules}
                        ads={ads}
                    />
                </div>
            </ModulesContextProvider>
        </ProductTaggingContextProvider>
    );

    const belowFoldSections = (
        <>
            <div>
                <ProjectSharesAndTags />
                <ProjectDesktopWebsiteCTA />
                <ProjectPageContent />
            </div>

            <div className="my-10 flex flex-col gap-10 md:mt-20 md:mb-51.25 md:gap-[13.75rem]">
                <Suspense fallback={null}>
                    <PageBottomSection openLogin={openLogin} />
                </Suspense>
            </div>
        </>
    );

    return (
        <>
            <Container background="gray">
                <PickerContext
                    containerRef={modulesContainerRef}
                    projectId={project.ID}
                    projectLink={project.Link}
                    bannerUrl={project.Banner.imageUrl}
                    isDesktop={isDesktop}
                    isEnabled={false}
                >
                    <ProjectPageHeader
                        canDownloadBannerImage={canDownloadBannerImage}
                    />

                    {/* ModuleAds already handles lazy loading for deferred modules internally */}
                    {projectModules}
                    {isMobile ? (
                        <LazySection
                            rootMargin="300px"
                            fallback={<BelowFoldSkeleton />}
                        >
                            {belowFoldSections}
                        </LazySection>
                    ) : (
                        belowFoldSections
                    )}
                </PickerContext>
            </Container>
        </>
    );
};

/**
 * Page-level chrome shared by both the legacy and revamp variants: the Projects
 * nav category, cross-country clone state, the BM staff edit shortcut and the
 * rotate-refresh. Runs in the switch so it fires regardless of which variant
 * renders.
 */
const useProjectPageChrome = () => {
    const project = useProjectPreviewRouteData();
    useRefreshOnRotate();

    const { setCategory } = useCurrentCategory();
    const setCountryData = useSetAtom(setCloneCountryStateAtom);
    const { canEditProjects } = useEditPermissions(project.Professional.ID);

    useEffect(() => {
        setCountryData({
            urlSegment: project?.URLSegment,
            currentCountry: project?.Professional?.Country,
            pageType: 'project',
            associatedPages: project?.AssociatedPages.edges.length
                ? project?.AssociatedPages
                : project?.Professional?.ProfessionalAssociatedPages,
        });
    }, [project, setCountryData]);

    useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    useSetEditMode(
        getBMPagePath(
            project.Professional.URLSegment,
            'projects',
            project.URLSegment,
        ),
        canEditProjects,
    );
};

/**
 * The revamp page skips ProjectPageContent (and its BM admin chrome), so re-wire
 * the DetailPageButtons (edit-mode toggle + copy link + the AP-admin in-directory
 * and pin-to-top toggles) here. useProjectPageChrome already handles the global
 * edit mode via useSetEditMode.
 */
const ProjectRevampWithAdmin = () => {
    const project = useProjectPreviewRouteData();
    // The template preview route reuses this component but its loader has no
    // isPinnedToDirectory, so it arrives undefined there despite the type.
    const { isPinnedToDirectory = false } = useLoaderData<typeof loader>();
    const { canEditProjects } = useEditPermissions(project.Professional.ID);
    const canCopyLink = usePermissions(PermissionEnum.BmMarketingView);
    const { visibilityToggle, pinToggle } = useProjectDirectoryAdminToggles(
        project,
        isPinnedToDirectory,
    );
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment,
    );

    return (
        <>
            <DetailPageButtons
                id={project.ID}
                editLink={projectEditPath}
                shareableLink={project.Link}
                canEdit={canEditProjects}
                canCopyLink={canCopyLink}
                visibilityToggle={visibilityToggle}
                pinToggle={pinToggle}
            />
            <ProjectPageRevamp />
        </>
    );
};

/**
 * Switches between the legacy project page and the editorial revamp behind the
 * `revamp_2026` CMS feature flag. The loader is shared and unchanged.
 * In local dev only, `?revamp=1` force-renders the revamp so the page can be
 * QA'd before the flag is activated on the team server.
 */
const ProjectPage = () => {
    const { detailsRevamp } = useLoaderData<typeof loader>();
    const [searchParams] = useSearchParams();
    const project = useProjectPreviewRouteData();

    // Published once for the route so every gate opened from this page shows
    // the project, rather than each CTA re-deriving the banner itself.
    useGateSubject({
        kind: 'project',
        id: project.ID,
        title: project.Title,
        hero: project.Banner?.imageUrl ?? undefined,
    });
    const showRevamp =
        detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1'));

    useProjectPageChrome();

    return showRevamp ? <ProjectRevampWithAdmin /> : <ProjectPageLegacy />;
};

export default ProjectPage;
